import { useCallback } from "react";
import { useSearchParams } from "react-router-dom";
import { useUser } from "@clerk/react";
import { ScopeWrapper } from "@/components/ui/scope-wrapper";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { CallsTab } from "./CallsTab";
import { PlansTab } from "./PlansTab";
import type { CallFilters, OpsMcpCall } from "./types";

const STATUSES: readonly OpsMcpCall["status"][] = ["ok", "error", "unknown_org"];

/**
 * Staff-only Activity page (/manager/activity): what happened through the
 * CS MCP server — every call by a person (Claude Code with the uplodio-ops
 * plugin) or a key (a scheduled run), which tool, on which client, how it
 * ended — and how the client plans changed, version by version. Both are
 * read from amy (/eve/v1/ops/activity/*, lib/ops/activity.ts); nothing here
 * writes. The tab and the filters live in the URL, so a link from the Success
 * board's client sheet opens one client's plan history.
 */
export default function ActivityPage() {
  const { user, isLoaded } = useUser();
  const isStaff = Boolean((user?.publicMetadata as Record<string, unknown> | undefined)?.uplodio_member);
  const [params, setParams] = useSearchParams();
  const tab = params.get("tab") === "plans" ? "plans" : "calls";
  const status = params.get("status");
  const filters: CallFilters = {
    caller: params.get("caller"),
    org_id: params.get("org"),
    tool: params.get("tool"),
    status: STATUSES.includes(status as OpsMcpCall["status"]) ? (status as OpsMcpCall["status"]) : null,
    changes: params.get("changes") === "1",
  };

  /** Set (or clear, with null) URL params in one history entry that replaces the current one. */
  const setParam = useCallback(
    (updates: Record<string, string | null>) =>
      setParams(
        (prev) => {
          const out = new URLSearchParams(prev);
          for (const [key, value] of Object.entries(updates)) {
            if (value) out.set(key, value);
            else out.delete(key);
          }
          return out;
        },
        { replace: true }
      ),
    [setParams]
  );

  if (!isLoaded) return null;
  if (!isStaff) {
    return <div className="flex h-full items-center justify-center p-6 text-sm text-muted-foreground">This page is for Uplodio staff only.</div>;
  }
  return (
    <div className="mx-auto flex w-full max-w-7xl flex-col gap-6 p-4 md:p-6">
      <div>
        <h1 className="text-2xl font-semibold">Activity</h1>
        <p className="text-sm text-muted-foreground">Every call on the CS MCP server, by person or key, and every change to a client plan.</p>
      </div>
      <Tabs value={tab} onValueChange={(v) => setParam({ tab: v === "plans" ? "plans" : null })} className="gap-6">
        <TabsList variant="underline" className="w-full justify-start border-b">
          <ScopeWrapper>
            <TabsTrigger value="calls">Tool calls</TabsTrigger>
          </ScopeWrapper>
          <ScopeWrapper>
            <TabsTrigger value="plans">Plan changes</TabsTrigger>
          </ScopeWrapper>
        </TabsList>
        <TabsContent value="calls">
          <CallsTab
            filters={filters}
            onFilter={(key, value) => setParam({ [key === "org_id" ? "org" : key]: value })}
            onClear={() => setParam({ caller: null, org: null, tool: null, status: null, changes: null })}
          />
        </TabsContent>
        <TabsContent value="plans">
          <PlansTab slug={params.get("client")} onPick={(slug) => setParam({ client: slug })} />
        </TabsContent>
      </Tabs>
    </div>
  );
}
